import { Spinner, ErrorMessage, ModalInjectedProps } from '@sketch/components'
import {
  ProjectDetailsFragment,
  ShareInfoFragment,
  StorageItemFragment,
  useGetShareQuery,
} from '@sketch/gql-types'
import React, { useEffect, useState } from 'react'
import {
  Footnote,
  FootnoteText,
  ModalBody,
} from './MoveStorageItemModal.styles'

import {
  MoveStorageItemModalFooter,
  MoveStorageItemModalLayout,
} from './MoveStorageItemModalLayout'
import {
  ProjectPickerContent,
  TreeStateProvider,
  TreeNodeTypes,
  useIsCreatingProject,
  useTreeState,
} from '../../../projects/modals/ProjectPickerContent'
import { useMoveStorageItem } from './useMoveStorageItem'
import {
  useGetProjectDetailsQuery,
  useGetProjectInSidebarQuery,
  useGetWorkspaceQuery,
} from '@sketch/gql-types/src/generated/gqlTypes/apollo'
import {
  MoveStorageItemModalHeader,
  MoveStorageItemModalHeaderLoading,
} from './MoveStorageItemModalHeader'
import { CurrentItemState } from '../../../projects/modals/ProjectPickerContent/state.base'
import { canAccessListChange } from './getLocation'

const useCurrentTreeState = () => {
  const treeState = useTreeState()

  const selectedNode = treeState.selected
  const [, setSelected] = useState(treeState.selected)

  useEffect(() => {
    const handleUpdate = () => {
      setSelected(treeState.selected)
    }

    return treeState.registerUpdatesListener(handleUpdate)
  }, [treeState])

  const currentNode = treeState.currentDestination
  const currentIds = treeState.currentIds
  const current = treeState.current

  const canAccessChange = selectedNode?.payload
    ? canAccessListChange(selectedNode.payload, treeState)
    : false

  return {
    selectedNode,
    currentNode,
    currentIds,
    current,
    canAccessListChange: canAccessChange,
  }
}

interface MoveModalFooterProps {
  onClick: (data: TreeNodeTypes) => void
  loading: boolean
  itemType: 'Document' | 'Folder'
}

export const MoveModalFooter = (props: MoveModalFooterProps) => {
  const { onClick, loading } = props
  // WARNING: Use useCurrentTreeState subscribes to all changes in the tree state
  // and will re-render the component on every change and can lead to severely
  // degraded performance issues on large trees if not used correctly.
  //
  // To use it correctly - use it in the lowest possible node of React components tree
  // to avoid unnecessary re-renders of other components.
  const { selectedNode, current, currentIds, canAccessListChange } =
    useCurrentTreeState()

  const { isCreatingProject } = useIsCreatingProject()

  const payload = selectedNode?.payload
  const isEnabled =
    !!payload?.identifier &&
    (current.originType === 'stashed' ||
      payload?.identifier !== currentIds.currentId)

  return (
    <MoveStorageItemModalFooter
      indicateNextStep={canAccessListChange}
      disabled={!isEnabled}
      loading={loading}
      dimmed={isCreatingProject}
      onClick={() => {
        if (!payload) return
        onClick(payload)
      }}
    >
      <Footnote>
        <FootnoteText>
          {selectedNode?.payload &&
          (current.originType === 'stashed' ||
            selectedNode.payload.identifier !== currentIds.currentId) ? (
            <>
              Move to <b>{selectedNode.payload.name}</b>
            </>
          ) : (
            <>
              Select a <b>project</b>
            </>
          )}
        </FootnoteText>
      </Footnote>
    </MoveStorageItemModalFooter>
  )
}

interface MoveModalInnerProps {
  item: ShareInfoFragment | ProjectDetailsFragment
  hideModal: () => void
}

const MoveModalInner = (props: MoveModalInnerProps) => {
  const { item, hideModal } = props
  const [moveStorageItem, { loading, ConfirmationModal }] = useMoveStorageItem(
    item,
    hideModal
  )

  if (ConfirmationModal) {
    return <ConfirmationModal loading={loading} />
  }

  return (
    <MoveStorageItemModalLayout>
      <MoveStorageItemModalHeader item={item} />
      <ProjectPickerContent />
      <MoveModalFooter
        itemType={item.__typename === 'Share' ? 'Document' : 'Folder'}
        loading={loading}
        onClick={data => moveStorageItem(data)}
      />
    </MoveStorageItemModalLayout>
  )
}

export interface MoveStorageItemModalProps extends ModalInjectedProps {
  item: Pick<StorageItemFragment, 'identifier' | '__typename'>
  workspaceIdentifier: string
  parentProjectIdentifier: string | null
  originType: 'stashed' | 'standard'
}

export const MoveStorageItemModal: React.FC<
  MoveStorageItemModalProps
> = props => {
  const {
    item,
    workspaceIdentifier,
    parentProjectIdentifier,
    hideModal,
    originType = 'standard',
  }: typeof props = props

  // this query most likely will return a cached value
  const workspaceQuery = useGetWorkspaceQuery({
    variables: { identifier: workspaceIdentifier },
  })

  // this query most likely will return a cached value
  const parentProjectQuery = useGetProjectInSidebarQuery({
    variables: { shortId: parentProjectIdentifier || '' },
    skip: !parentProjectIdentifier,
  })

  const shareQuery = useGetShareQuery({
    variables: { shortId: item.identifier },
    skip: item.__typename !== 'Share',
  })

  const projectQuery = useGetProjectDetailsQuery({
    variables: { projectIdentifier: item.identifier },
    skip: item.__typename !== 'Project',
  })

  const loading =
    shareQuery.loading ||
    projectQuery.loading ||
    workspaceQuery.loading ||
    parentProjectQuery.loading
  const error =
    shareQuery.error ||
    projectQuery.error ||
    workspaceQuery.error ||
    parentProjectQuery.error

  if (loading) {
    return (
      <MoveStorageItemModalLayout>
        <MoveStorageItemModalHeaderLoading />
        <ModalBody>
          <Spinner.Flex primary />
        </ModalBody>
        <MoveStorageItemModalFooter disabled loading />
      </MoveStorageItemModalLayout>
    )
  }

  const itemFull = shareQuery.data?.share || projectQuery.data?.project
  const workspace = workspaceQuery.data?.workspace
  const parentProject = parentProjectQuery.data?.project || null

  if (error || !itemFull || !workspace) {
    return (
      <MoveStorageItemModalLayout>
        <MoveStorageItemModalHeaderLoading />
        <ModalBody>
          <ErrorMessage.Generic />
        </ModalBody>
        <MoveStorageItemModalFooter disabled />
      </MoveStorageItemModalLayout>
    )
  }

  const treeStateContext: CurrentItemState = {
    item: itemFull,
    workspace,
    project: parentProject,
    originType,
  }

  return (
    <TreeStateProvider current={treeStateContext}>
      <MoveModalInner item={itemFull} hideModal={hideModal} />
    </TreeStateProvider>
  )
}
